import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const ContentContext = createContext({ content: {}, ready: false, refresh: () => {} });

export function ContentProvider({ children }) {
  const [content, setContent] = useState({});
  const [ready, setReady] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/content");
      setContent(data || {});
    } catch {
      setContent({});
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <ContentContext.Provider value={{ content, ready, refresh }}>
      {children}
    </ContentContext.Provider>
  );
}

export function useContent() {
  return useContext(ContentContext).content;
}

export function useContentRefresh() {
  return useContext(ContentContext).refresh;
}

/**
 * Small component that reads a content key with a fallback.
 * Usage:  <T k="home.hero.title_1" fallback="Own a Piece" />
 */
export function T({ k, fallback = "", as: Tag = "span", className, style }) {
  const content = useContent();
  const value = content?.[k];
  return (
    <Tag className={className} style={style}>
      {value != null && value !== "" ? value : fallback}
    </Tag>
  );
}

/**
 * Hook version — returns the resolved string.
 */
export function useT(k, fallback = "") {
  const content = useContent();
  const value = content?.[k];
  return value != null && value !== "" ? value : fallback;
}
